'use client';

import React from 'react';

interface DomainConfigProps {
  publicIp: string;
  localIp: string;
}

export function DomainConfigSection({ publicIp, localIp }: DomainConfigProps): React.JSX.Element {
  return (
    <div className="w-full space-y-6">
      {/* Título y Cards de Estado de Conexión */}
      <div>
        <h2 className="text-xl font-bold text-white mb-4">Configuración de Dominio y DNS</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* IP Pública */}
          <div className="p-4 rounded-xl bg-[#12111F]/90 border border-[#2A2545] shadow-sm">
            <div className="flex items-center space-x-2 text-xs font-mono text-amber-400 mb-1">
              <span>🖥️ IP Pública Detectada (Esta PC)</span>
            </div>
            <div className="text-xl font-black font-mono text-emerald-400 tracking-wide">
              {publicIp || '181.15.84.50'}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">IP actual de tu conexión de Internet.</p>
          </div>

          {/* IP Local */}
          <div className="p-4 rounded-xl bg-[#12111F]/90 border border-[#2A2545] shadow-sm">
            <div className="flex items-center space-x-2 text-xs font-mono text-cyan-400 mb-1">
              <span>📡 IP Local (Tu Red)</span>
            </div>
            <div className="text-xl font-black font-mono text-cyan-400 tracking-wide">
              {localIp || '192.168.1.4'}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">IP privada para configurar el router.</p>
          </div>

          {/* DNS DonWeb */}
          <div className="p-4 rounded-xl bg-[#12111F]/90 border border-[#2A2545] shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center space-x-2 text-xs font-mono text-purple-400 mb-1">
                <span>🌐 DNS DonWeb (planetakaio.com.ar)</span>
              </div>
              <div className="text-xl font-black font-mono text-emerald-400 tracking-wide">
                {publicIp || '181.15.84.50'}
              </div>
            </div>
            <div className="mt-2 inline-flex items-center space-x-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] font-mono w-fit">
              <span>✔</span>
              <span>Sincronizado correctamente</span>
            </div>
          </div>
        </div>
      </div>

      {/* Paso 1: Configurar Registros en la Zona DNS de DonWeb */}
      <div className="rounded-2xl bg-[#12111F]/80 border border-[#2A2545] overflow-hidden">
        <div className="p-5 border-b border-[#2A2545]">
          <div className="flex items-center space-x-2 text-amber-400 text-xs font-bold uppercase tracking-wider mb-1">
            <span className="w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center text-[11px]">
              1
            </span>
            <span>Paso 1: Configurar Registros en la Zona DNS de DonWeb</span>
          </div>
          <p className="text-xs text-slate-400">
            Ingresa a la sección de <strong className="text-slate-200">Mis Zonas DNS</strong> en tu panel de control de DonWeb para el dominio <strong className="text-amber-400">planetakaio.com.ar</strong> y agrega o edita los siguientes registros:
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-black/40 text-slate-400 uppercase text-[10px] tracking-wider border-b border-[#2A2545]">
              <tr>
                <th className="px-5 py-3">TIPO</th>
                <th className="px-5 py-3">NOMBRE (HOST)</th>
                <th className="px-5 py-3">VALOR (DESTINO)</th>
                <th className="px-5 py-3">TTL</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#2A2545]/60 text-slate-300">
              <tr className="hover:bg-white/5 transition-colors">
                <td className="px-5 py-3 font-bold text-amber-400">A</td>
                <td className="px-5 py-3 text-white">@ o vacío (planetakaio.com.ar)</td>
                <td className="px-5 py-3 text-emerald-400 font-bold">{publicIp || '181.15.84.50'}</td>
                <td className="px-5 py-3 text-slate-500">3600 (o por defecto)</td>
              </tr>
              <tr className="hover:bg-white/5 transition-colors">
                <td className="px-5 py-3 font-bold text-amber-400">A</td>
                <td className="px-5 py-3 text-white">www (www.planetakaio.com.ar)</td>
                <td className="px-5 py-3 text-emerald-400 font-bold">{publicIp || '181.15.84.50'}</td>
                <td className="px-5 py-3 text-slate-500">3600</td>
              </tr>
              <tr className="hover:bg-white/5 transition-colors">
                <td className="px-5 py-3 font-bold text-cyan-400">CNAME</td>
                <td className="px-5 py-3 text-white">api (api.planetakaio.com.ar)</td>
                <td className="px-5 py-3 text-amber-300">planetakaio.com.ar</td>
                <td className="px-5 py-3 text-slate-500">3600</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Paso 2: Redirección de Puertos (Port Forwarding) en tu Router */}
      <div className="rounded-2xl bg-[#12111F]/80 border border-[#2A2545] p-6 space-y-4 text-xs text-slate-300">
        <div className="flex items-center space-x-2 text-cyan-400 font-bold uppercase tracking-wider">
          <span className="w-5 h-5 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-[11px]">
            2
          </span>
          <span>Paso 2: Redirección de Puertos (Port Forwarding) en tu Router</span>
        </div>
        <p className="text-slate-400 leading-relaxed">
          Como el servidor corre en esta computadora local, debes permitir que las conexiones del exterior ingresen a través de tu router:
        </p>

        <ol className="list-decimal list-inside space-y-2 text-slate-300 pl-1 leading-relaxed">
          <li>
            Accede al panel de control de tu router (normalmente abriendo{' '}
            <code className="text-amber-400 font-bold">http://192.168.0.1</code> o{' '}
            <code className="text-amber-400 font-bold">http://192.168.1.1</code> en el navegador).
          </li>
          <li>
            Busca la sección llamada <strong className="text-white">Port Forwarding (Redirección de Puertos)</strong>,{' '}
            <strong className="text-white">Virtual Server (Servidor Virtual)</strong> o{' '}
            <strong className="text-white">NAT</strong>.
          </li>
          <li>
            Configura una nueva regla con los siguientes datos:
            <ul className="list-disc list-inside pl-5 mt-2 space-y-1 font-mono text-[11px] text-slate-300">
              <li>
                <span className="text-slate-400">IP Interna / IP Destino:</span>{' '}
                <strong className="text-cyan-400">{localIp || '192.168.1.4'}</strong> (la IP de esta PC).
              </li>
              <li>
                <span className="text-slate-400">Puerto Externo:</span>{' '}
                <strong className="text-white">80</strong> (y opcionalmente 443 para HTTPS).
              </li>
              <li>
                <span className="text-slate-400">Puerto Interno:</span>{' '}
                <strong className="text-amber-400">3000</strong> (el puerto donde corre Planeta Kaio).
              </li>
              <li>
                <span className="text-slate-400">Protocolo:</span>{' '}
                <strong className="text-white">TCP o AMBOS (TCP/UDP)</strong>.
              </li>
            </ul>
          </li>
        </ol>

        {/* Nota sobre IP Dinámica */}
        <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] flex items-start space-x-2">
          <span className="text-sm">⚠️</span>
          <p className="leading-relaxed">
            <strong>Nota sobre IP Pública Dinámica:</strong> Las conexiones de Internet para hogares suelen cambiar la IP pública periódicamente. Si la IP cambia, tendrás que actualizar el valor de los registros A en DonWeb con la nueva IP que te muestre este panel.
          </p>
        </div>
      </div>
    </div>
  );
}
